/**
 * The camera: a ground-plane projection. The sea is the plane y = 0, the eye is at the rail
 * `eyeH` metres up looking at the horizon. A thing at lateral `x` and distance `d` (metres)
 * and height `hgt` above the water lands on screen at
 *
 *   sy = horizonY + f * (eyeH - hgt) / d      sx = cx + f * x / d      scale = f / d
 *
 * then the boat's sway is applied: heave (whole scene up/down) and roll (rotation about
 * the screen centre). Everything that paints or hit-tests goes through `project`, so the
 * renderer and the engine always agree on where a coconut is.
 */
export interface View {
  w: number;
  h: number;
  /** screen y of the horizon before sway */
  horizonY: number;
  /** focal length in px: screen size of one metre at one metre */
  f: number;
  eyeH: number;
  /** radians, positive = clockwise */
  roll: number;
  /** px, positive = scene moves down */
  heave: number;
}

export interface Projected {
  sx: number;
  sy: number;
  /** px per metre at this distance */
  s: number;
}

export const EYE_H = 2.5;

export function makeView(w: number, h: number): View {
  // the horizon sits at 40% of the height; f is tied to height so landscape doesn't magnify.
  // f = 0.9h puts 5 m at 85% of the screen and 50 m at 44.5%: the near zone is usable and
  // a far coconut is still a few px (checked on the Razr, 2026-10-09)
  const horizonY = h * 0.4;
  const f = h * 0.9;
  return { w, h, horizonY, f, eyeH: EYE_H, roll: 0, heave: 0 };
}

/** boat motion at time t: a slow swell and a faster chop */
export function swayAt(v: View, t: number): void {
  const T = 2 * Math.PI;
  v.roll = (1.4 * Math.sin((T * t) / 7.3) + 0.45 * Math.sin((T * t) / 2.4 + 1.1)) * (Math.PI / 180);
  v.heave = (0.011 * Math.sin((T * t) / 7.3 + 0.9) + 0.004 * Math.sin((T * t) / 2.4)) * v.h;
}

/** metres of sea visible either side of centre at distance d (before sway) */
export function halfWidthAt(v: View, d: number): number {
  return ((v.w / 2) * d) / v.f;
}

const out: Projected = { sx: 0, sy: 0, s: 0 };

/** project a world point; returns a shared scratch object, copy it if you keep it */
export function project(v: View, x: number, d: number, hgt = 0): Projected {
  const s = v.f / d;
  const px = v.w / 2 + s * x;
  const py = v.horizonY + s * (v.eyeH - hgt);
  // sway: heave then roll about the screen centre
  const cx = v.w / 2;
  const cy = v.h / 2;
  const dx = px - cx;
  const dy = py + v.heave - cy;
  const c = Math.cos(v.roll);
  const sn = Math.sin(v.roll);
  out.sx = cx + dx * c - dy * sn;
  out.sy = cy + dx * sn + dy * c;
  out.s = s;
  return out;
}

/** the sea point under a screen point, or null if it is at or above the horizon */
export function unproject(v: View, sx: number, sy: number): { x: number; d: number } | null {
  const cx = v.w / 2;
  const cy = v.h / 2;
  const c = Math.cos(-v.roll);
  const sn = Math.sin(-v.roll);
  const dx = sx - cx;
  const dy = sy - cy;
  const px = cx + dx * c - dy * sn;
  const py = cy + dx * sn + dy * c - v.heave;
  const below = py - v.horizonY;
  if (below <= 0.5) return null;
  const d = (v.f * v.eyeH) / below;
  const x = ((px - cx) * d) / v.f;
  return { x, d };
}

/** the bearing (degrees right of straight ahead) whose line on the water passes under screen x */
export function azimuthAt(v: View, sx: number): number {
  return (Math.atan((sx - v.w / 2) / v.f) * 180) / Math.PI;
}

/**
 * The throw elevation a screen row stands for: the rail is a flat 8°, the horizon the 45° that
 * carries farthest, and the sky above it a lob up to 75°. So where you press says how high as
 * well as which way.
 */
export function elevationAt(v: View, sy: number): number {
  if (sy >= v.horizonY) {
    const u = Math.min(1, (sy - v.horizonY) / Math.max(1, v.h - v.horizonY));
    return 45 - u * (45 - 8);
  }
  const t = Math.min(1, (v.horizonY - sy) / Math.max(1, v.horizonY));
  return 45 + t * (75 - 45);
}
